import React, { useState, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import CustomTooltip from "./Tooltip";
import { SaleData, StatBox } from "./StatsBox";

interface MonthlyRevenueProps {
  data: SaleData[];
}
const monthLabels = [
  "Janv.",
  "Fév.",
  "Mars",
  "Avril",
  "Mai",
  "Juin",
  "Juil.",
  "Août",
  "Sept.",
  "Oct.",
  "Nov.",
  "Déc.",
];

const MonthlyRevenue: React.FC<MonthlyRevenueProps> = ({ data }) => {
  const today = new Date();
  const currentYear = today.getFullYear();

  const paidSales = data.filter(
    (p) => p.paymentDate && p.paymentDate.getTime() <= today.getTime()
  );

  const availableYears = useMemo(
    () =>
      Array.from(
        new Set(
          paidSales
            .map((p) => p.paymentDate?.getFullYear())
            .filter((y): y is number => y !== null)
        )
      ).sort((a, b) => b - a),
    [paidSales]
  );

  const [selectedYear, setSelectedYear] = useState(currentYear);
  // Nombre de mois passés dans l'année sélectionnée
  const monthsElapsed =
    selectedYear === currentYear ? today.getMonth() + 1 : 12;
  // Chiffre d'affaires total pour l'année sélectionnée
  const totalRevenueYear = paidSales
    .filter((p) => p.paymentDate?.getFullYear() === selectedYear)
    .reduce((sum, p) => sum + p.amount, 0);

  const dataMonthlyAll = useMemo(
    () =>
      monthLabels.map((label, idx) => ({
        name: label,
        value: paidSales
          .filter(
            (p) =>
              p.paymentDate?.getFullYear() === selectedYear &&
              p.paymentDate.getMonth() === idx
          )
          .reduce((sum, p) => sum + p.amount, 0),
      })),
    [paidSales, selectedYear]
  );

  return (
    <div className="flex flex-col gap-6 rounded-lg flex-1">
      <StatBox
        label="Chiffre d’affaire moyen"
        value={(totalRevenueYear / (monthsElapsed || 1)).toLocaleString(
          "fr-FR",
          {
            style: "currency",
            currency: "EUR",
          }
        )}
      />

      <div className="flex flex-col gap-6 bg-white border border-gray-200 rounded-lg p-4 flex-1">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-xl">Chiffre d’affaire mensuel</h3>
          <select
            className="border border-gray-300 rounded p-1 text-sm"
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <div style={{ width: "100%", height: "100%" }}>
          <ResponsiveContainer>
            <BarChart data={dataMonthlyAll}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#F1F1F1"
                vertical={false}
              />
              <XAxis dataKey="name" interval={0} />
              <YAxis
                domain={[0, "dataMax"]}
                tickFormatter={(value) =>
                  value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value
                }
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="value"
                fill="#99C3FF"
                radius={[4, 4, 0, 0]}
                maxBarSize={40}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default MonthlyRevenue;
